import {
  HomeOutlined,
  TrendingUpOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Container,
  Paper,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { collection, getCountFromServer, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useMicrospecDynamicUIContext } from '../../MicrospecDynamicUI/GlobalProvider';

// ─── Stat Card ───────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, loading, color }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: color || theme.palette.primary.main,
          color: '#fff',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        {loading ? (
          <>
            <Skeleton variant="text" width={60} height={32} />
            <Skeleton variant="text" width={80} height={18} />
          </>
        ) : (
          <>
            <Typography variant="h5" fontWeight={800}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </>
        )}
      </Box>
    </Paper>
  );
};

// ─── Owners Dashboard ────────────────────────────────────────────────────────
const OwnersDashboard = () => {
  const theme = useTheme();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const userProfile = cloudProvider.userProfile?.attributes;

  const [stats, setStats] = useState({
    totalProperties: 0,
    activeProperties: 0,
    pendingProperties: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const firstName = userProfile?.firstName || userProfile?.first_name || '';
  const lastName = userProfile?.lastName || userProfile?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const photoUrl = userProfile?.photoURL || userProfile?.photo_url || '';

  useEffect(() => {
    if (!firestore?.instance || !userProfile?.uid) return;

    const fetchStats = async () => {
      try {
        const db = firestore.instance;
        const uid = userProfile.uid;
        const propertiesRef = collection(db, 'properties');

        // Total properties by this owner
        const totalSnap = await getCountFromServer(
          query(propertiesRef, where('owner_id', '==', uid))
        );

        // Active & approved properties
        const activeSnap = await getCountFromServer(
          query(
            propertiesRef,
            where('owner_id', '==', uid),
            where('active', '==', true),
            where('approval_status', '==', 'approved')
          )
        );

        // Pending (not approved) properties
        const pendingSnap = await getCountFromServer(
          query(
            propertiesRef,
            where('owner_id', '==', uid),
            where('approval_status', '==', 'under_review')
          )
        );

        setStats({
          totalProperties: totalSnap.data().count,
          activeProperties: activeSnap.data().count,
          pendingProperties: pendingSnap.data().count,
        });
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [firestore?.instance, userProfile?.uid]);

  // Greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'صباح الخير';
    if (hour < 18) return 'مساء الخير';
    return 'مساء الخير';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Section */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.06,
            backgroundImage: 'radial-gradient(circle at 30% 30%, #fff 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar
            src={photoUrl}
            alt={fullName}
            sx={{
              width: 72,
              height: 72,
              border: '3px solid rgba(255,255,255,0.3)',
              fontSize: '1.8rem',
              bgcolor: 'rgba(255,255,255,0.2)',
            }}
          >
            {fullName.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={800}>
              {getGreeting()}، {firstName || 'مرحباً'}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85, mt: 0.5 }}>
              مرحباً بك في لوحة تحكم عقاركو
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Stats Grid */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        إحصائيات سريعة
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, 1fr)',
          },
          gap: 2,
          mb: 4,
        }}
      >
        <StatCard
          icon={<HomeOutlined sx={{ fontSize: 26 }} />}
          label="إجمالي العقارات"
          value={stats.totalProperties}
          loading={statsLoading}
          color={theme.palette.primary.main}
        />
        <StatCard
          icon={<VisibilityOutlined sx={{ fontSize: 26 }} />}
          label="عقارات منشورة"
          value={stats.activeProperties}
          loading={statsLoading}
          color="#2e7d32"
        />
        <StatCard
          icon={<TrendingUpOutlined sx={{ fontSize: 26 }} />}
          label="بانتظار الموافقة"
          value={stats.pendingProperties}
          loading={statsLoading}
          color="#ed6c02"
        />
      </Box>
    </Container>
  );
};

export default OwnersDashboard;
